/**
 * Univer Slides viewer / editor.
 *
 * When @univerjs/presets is installed the real Univer canvas is used.
 * Otherwise every slide is rendered with a scale-based HTML fallback that
 * replicates the absolute-positioned element layout from the ISlideData
 * snapshot. The fallback also supports inline text editing via contentEditable.
 */

import { CSSProperties, useEffect, useRef, useState } from "react";

type Props = {
  snapshot: object | null;
  className?: string;
  editable?: boolean;
  onSnapshotChange?: (snapshot: any) => void;
};

let UniverSlidesPreset: any = null;
let createUniver: any = null;
let defaultTheme: any = null;
let LocaleType: any = null;

try {
  // @ts-ignore – optional peer dependency
  const presets = require("@univerjs/presets");
  // @ts-ignore
  const slidePreset = require("@univerjs/presets/preset-slides-core");
  createUniver = presets.createUniver;
  defaultTheme = presets.defaultTheme;
  LocaleType = presets.LocaleType;
  UniverSlidesPreset = slidePreset.UniverSlidesPreset;
  // @ts-ignore
  require("@univerjs/presets/lib/styles/preset-slides-core.css");
} catch {
  // Univer not installed – fall back to HTML renderer
}

export const UniverSlidesViewer = ({
  snapshot,
  className,
  editable,
  onSnapshotChange,
}: Props) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const univerRef = useRef<any>(null);

  useEffect(() => {
    if (!snapshot || !containerRef.current) return;
    if (!createUniver || !UniverSlidesPreset) return;

    univerRef.current?.dispose?.();

    const { univerAPI } = createUniver({
      locale: LocaleType.EN_US,
      theme: defaultTheme,
      presets: [UniverSlidesPreset()],
      container: containerRef.current,
    });

    univerAPI.createSlide(snapshot);
    univerRef.current = univerAPI;

    return () => {
      univerRef.current?.dispose?.();
    };
  }, [snapshot]);

  if (!snapshot) return null;

  if (!createUniver || !UniverSlidesPreset) {
    return (
      <SlidesFallback
        snapshot={snapshot}
        className={className}
        editable={editable}
        onSnapshotChange={onSnapshotChange}
      />
    );
  }

  return (
    <div
      ref={containerRef}
      className={className}
      style={{ width: "100%", height: "100%", minHeight: 540 }}
    />
  );
};

// ---------------------------------------------------------------------------
// Scale-based HTML fallback
// ---------------------------------------------------------------------------

const ALIGN_MAP: ("left" | "center" | "right" | "justify")[] = [
  "left",
  "center",
  "right",
  "justify",
];

type ScaledSlideProps = {
  page: any;
  pw: number;
  ph: number;
  editable?: boolean;
  onElementChange?: (elemId: string, newText: string) => void;
};

/** A single slide rendered with absolutely-positioned elements, scaled to fit its container. */
const ScaledSlide = ({
  page,
  pw,
  ph,
  editable,
  onElementChange,
}: ScaledSlideProps) => {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const update = () => setScale(el.offsetWidth / pw);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [pw]);

  const bg: string = page.background?.rgb ?? "#0F0A1E";
  const order: string[] =
    page.pageElementOrder ?? Object.keys(page.pageElements ?? {});
  const elems: Record<string, any> = page.pageElements ?? {};

  const sorted = [...order].sort(
    (a, b) => (elems[a]?.zIndex ?? 0) - (elems[b]?.zIndex ?? 0)
  );

  return (
    <div
      ref={wrapRef}
      style={{
        position: "relative",
        width: "100%",
        paddingBottom: `${(ph / pw) * 100}%`,
        borderRadius: 6,
        overflow: "hidden",
        boxShadow: "0 4px 20px rgba(0,0,0,0.25)",
        outline: editable ? "2px solid #6B21D9" : undefined,
      }}
    >
      <div
        style={{
          position: "absolute",
          inset: 0,
          background: bg,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: pw,
            height: ph,
            transformOrigin: "top left",
            transform: `scale(${scale})`,
          }}
        >
          {sorted.map((id) => {
            const elem = elems[id];
            if (!elem) return null;

            const base: CSSProperties = {
              position: "absolute",
              left: elem.left,
              top: elem.top,
              width: elem.width,
              height: elem.height,
              zIndex: elem.zIndex ?? 0,
            };

            if (elem.elementType === 1) {
              const fill =
                elem.shape?.shapeProperties?.shapeBackgroundFill?.rgb ??
                "transparent";
              return <div key={id} style={{ ...base, background: fill }} />;
            }

            if (elem.elementType === 0) {
              const body = elem.richText?.body ?? {};
              const ts = body.textRuns?.[0]?.ts ?? {};
              const alignIdx: number =
                body.paragraphs?.[0]?.paragraphStyle?.horizontalAlign ?? 0;
              const text = (body.dataStream ?? "").replace(/\r\n$/, "");

              const sharedStyle: CSSProperties = {
                ...base,
                color: ts.cl?.rgb ?? "#ffffff",
                fontSize: ts.fs ?? 12,
                fontWeight: ts.bl ? 700 : 400,
                textAlign: ALIGN_MAP[alignIdx] ?? "left",
                overflow: "hidden",
                fontFamily:
                  "Inter, -apple-system, BlinkMacSystemFont, sans-serif",
                lineHeight: 1.3,
                padding: "0 4px",
              };

              if (editable) {
                return (
                  <div
                    key={id}
                    contentEditable
                    suppressContentEditableWarning
                    onBlur={(e) =>
                      onElementChange?.(id, e.currentTarget.textContent ?? "")
                    }
                    style={{
                      ...sharedStyle,
                      whiteSpace: "pre-wrap",
                      cursor: "text",
                      outline: "1px dashed rgba(107,33,217,0.55)",
                      outlineOffset: 1,
                    }}
                  >
                    {text}
                  </div>
                );
              }

              return (
                <div
                  key={id}
                  style={{
                    ...sharedStyle,
                    display: "flex",
                    alignItems: "center",
                    whiteSpace: "pre",
                  }}
                >
                  {text}
                </div>
              );
            }

            return null;
          })}
        </div>
      </div>
    </div>
  );
};

type FallbackProps = {
  snapshot: any;
  className?: string;
  editable?: boolean;
  onSnapshotChange?: (snapshot: any) => void;
};

const SlidesFallback = ({
  snapshot,
  className,
  editable,
  onSnapshotChange,
}: FallbackProps) => {
  const { pages, pageOrder } = (snapshot?.body ?? {}) as {
    pages: Record<string, any>;
    pageOrder: string[];
  };
  const pw: number = snapshot?.pageSize?.width ?? 960;
  const ph: number = snapshot?.pageSize?.height ?? 540;

  if (!pageOrder?.length) return null;

  const handleElementChange = (
    pageId: string,
    elemId: string,
    newText: string
  ) => {
    if (!onSnapshotChange) return;
    const next = JSON.parse(JSON.stringify(snapshot));
    const elem = next?.body?.pages?.[pageId]?.pageElements?.[elemId];
    if (!elem?.richText?.body) return;
    elem.richText.body.dataStream = newText + "\r\n";
    if (elem.richText.body.textRuns?.[0]) {
      elem.richText.body.textRuns[0].ed = newText.length;
    }
    onSnapshotChange(next);
  };

  return (
    <div className={className}>
      {pageOrder.map((pageId, idx) => {
        const page = pages?.[pageId];
        if (!page) return null;
        return (
          <div key={pageId} style={{ marginBottom: 28 }}>
            <p
              style={{
                fontSize: 11,
                color: "#6B7280",
                marginBottom: 6,
                fontFamily: "Inter, sans-serif",
              }}
            >
              Slide {idx + 1}
            </p>
            <ScaledSlide
              page={page}
              pw={pw}
              ph={ph}
              editable={editable}
              onElementChange={(elemId, text) =>
                handleElementChange(pageId, elemId, text)
              }
            />
          </div>
        );
      })}
    </div>
  );
};
